/**
 * A workspace, as somewhere you arrive rather than a heading you read.
 *
 * The shell offers workspaces; this is what is behind one. It surveys the
 * capabilities the principal may reach, grouped into the clusters the registry
 * declares, so a deep capability is entered from a place that explains what it
 * sits among — `workspace → cluster → capability` rather than a flat list where
 * an administrative screen and a whole area of the product look like peers.
 *
 * Everything here is read from `eligibleWorkspaces`. This component decides
 * nothing about permission; if it filtered the registry itself there would be
 * two implementations of eligibility, and the more permissive one would be the
 * defect.
 *
 * **An empty workspace is never rendered as a workspace.** Three different
 * silences are possible on a hub route and they are not the same news:
 *
 * ```
 * no such workspace     the key is not one of the six
 * nothing built         every destination in it is planned
 * nothing granted       destinations exist; this principal may reach none
 * ```
 *
 * Collapsing the last two would be the familiar lie in both directions: telling
 * somebody they were refused when the truth is it does not exist yet, or
 * telling them it does not exist when the truth is they were not granted it.
 */

import { Link } from "react-router-dom";
import { useSession } from "../session/SessionProvider";
import { eligibleWorkspaces, workspaceByKey } from "./navigation";

/** What a workspace with nothing eligible in it should say. */
function silenceOf(key: string): { title: string; body: string } {
  const workspace = workspaceByKey(key);
  const anythingBuilt = workspace?.groups.some((group) =>
    group.items.some((item) => item.availability === "available"),
  );
  return anythingBuilt === true
    ? {
        title: "Nothing here has been granted to you",
        body:
          "This area of the platform is built, and no capability in it has been granted to the organization you are acting as. Nothing was refused just now — there is simply nothing here this account may open. Ask whoever administers roles for this tenant.",
      }
    : {
        title: "This area is not built yet",
        body:
          "The workspace is part of the architecture and the platform does not serve it yet. This is not a permission you are missing: there is nothing here to be granted.",
      };
}

export function WorkspaceHub({ workspaceKey }: { workspaceKey: string }) {
  const session = useSession();
  const declared = workspaceByKey(workspaceKey);

  if (declared === undefined) {
    return (
      <div className="nx-state">
        <p className="nx-state__title">There is no such workspace.</p>
        <p>The address may be from an older version of Nexus.</p>
      </div>
    );
  }

  const workspace = eligibleWorkspaces(session.may).find((each) => each.key === workspaceKey);

  if (workspace === undefined) {
    const silence = silenceOf(workspaceKey);
    return (
      <div className="nx-hub">
        <header className="nx-hub__head">
          <p className="nx-hub__eyebrow">Workspace</p>
          <h2 className="nx-hub__title">{declared.label}</h2>
          <p className="nx-hub__summary">{declared.summary}</p>
        </header>
        <div className="nx-state">
          <p className="nx-state__title">{silence.title}</p>
          <p>{silence.body}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="nx-hub">
      <header className="nx-hub__head">
        <p className="nx-hub__eyebrow">Workspace</p>
        <h2 className="nx-hub__title">{workspace.label}</h2>
        <p className="nx-hub__summary">{workspace.summary}</p>
      </header>

      {workspace.groups.map((cluster) => (
        <section className="nx-hub__cluster" key={cluster.label} aria-labelledby={`cluster-${cluster.label}`}>
          <h3 className="nx-hub__cluster-label" id={`cluster-${cluster.label}`}>
            {cluster.label}
          </h3>
          <div className="nx-tiles">
            {cluster.items.map((destination) => {
              const Glyph = destination.icon;
              return (
                <article className="nx-tile" key={destination.to}>
                  <span className="nx-tile__glyph" aria-hidden="true">
                    <Glyph />
                  </span>
                  {/* The link carries the label and nothing else, so its
                      accessible name is the capability's name. The summary is
                      adjacent text the link's overlay covers rather than
                      swallows. */}
                  <h4 className="nx-tile__name">
                    <Link className="nx-tile__link" to={destination.to}>
                      {destination.label}
                    </Link>
                  </h4>
                  <p className="nx-tile__summary">{destination.summary}</p>
                </article>
              );
            })}
          </div>
        </section>
      ))}
    </div>
  );
}
